import { useEffect, useRef } from 'react'
import { createRoot } from 'react-dom/client'
import { ApolloProvider, useApolloClient } from '@apollo/client'
import { ThemeProvider } from '@center/ui-kit'
import { useMap } from './IndoorProvider'

import InfoPopup from '../Popup'

const usePopup = ({ PopupContent, onDetailsClick = (marker) => { } }) => {
  const apolloClient = useApolloClient()
  const { transformWrapperState, popupAnchorRef }: any = useMap()
  const { scale } = transformWrapperState
  const markerEl = useRef(null)
  const popupAnchorRootEl: any = useRef(null)
  const closePopupTimeoutEl: any = useRef(null)

  const clearClosePopupTimeout = () => {
    if (!closePopupTimeoutEl.current) {
      return
    }

    clearTimeout(closePopupTimeoutEl.current)
    closePopupTimeoutEl.current = null
  }

  const renderEmptyPopup = () => {
    try {
      popupAnchorRootEl.current?.render(null)
    } catch (e) {
      console.log(e)
    }
  }

  useEffect(() => {
    if (popupAnchorRootEl.current) {
      renderEmptyPopup()
    }
  }, [scale])

  useEffect(() => {
    return () => {
      clearClosePopupTimeout()
      markerEl.current = null

      if (!popupAnchorRootEl.current) {
        return
      }

      try {
        popupAnchorRootEl.current.unmount()
      } catch (e) {
        console.log(e)
      }

      popupAnchorRootEl.current = null
    }
  }, [])

  const renderPopup = (content) => {
    clearClosePopupTimeout()

    try {
      if (!popupAnchorRootEl.current) {
        const popupAnchor = popupAnchorRef?.current
        if (!popupAnchor) return
        popupAnchorRootEl.current = createRoot(popupAnchor)
      }
      popupAnchorRootEl.current.render(content)
    } catch (e) {
      console.log(e)
    }
  }

  const closePopup = (id) => {
    clearClosePopupTimeout()

    closePopupTimeoutEl.current = setTimeout(() => {
      closePopupTimeoutEl.current = null

      if (markerEl.current === id) {
        closePopup(id)
      } else if (!markerEl.current && popupAnchorRootEl.current) {
        renderEmptyPopup()
      }
    }, 500)
  }

  const handleDetailsClick = (marker) => {
    clearClosePopupTimeout()
    renderEmptyPopup()
    markerEl.current = null
    onDetailsClick(marker)
  }

  const openPopup = (markerData) => {
    const device = markerData.device || markerData
    const hasChildren = device.children && device.children.length > 0
    const isRackType = device.model?.type && ['rack', 'mer', 'ser'].includes(device.model.type)
    
    const shouldShowParentPopup = hasChildren && !isRackType

    renderPopup(
      <ApolloProvider client={apolloClient}>
        <ThemeProvider>
          <InfoPopup
            content={shouldShowParentPopup ? null : <PopupContent marker={markerData} />}
            parentDevice={shouldShowParentPopup ? device : null}
            sitecolor={markerData.color || device.color || '#464646'}
            onPopupEnter={() => onMarkerEnter(markerData)}
            onPopupLeave={onMarkerLeave}
            onDetailsClick={() => handleDetailsClick(markerData)}
          />
        </ThemeProvider>
      </ApolloProvider>
    )
  }

  const onMarkerEnter = (markerData) => {
    const markerId = markerData.id || markerData.uuid
    if (markerEl.current !== markerId) {
      markerEl.current = markerId
      openPopup(markerData)
    }
  }

  const onMarkerLeave = () => {
    closePopup(markerEl.current)
    markerEl.current = null
  }

  return {
    onMarkerEnter,
    onMarkerLeave,
  }
}

export default usePopup